import { TrackingService } from '../services/TrackingService'
import { FormSubmitted } from '../events/FormSubmitted'
import type { FormContext, FormField, StepContext } from '../types'

export interface UseFormSubmittedProps {
  formContext: FormContext
  stepContext: StepContext
  field: FormField[]
}

/**
 * Hook for tracking form submission events
 *
 * Tracks when a user successfully submits a form. This is the final step
 * in form tracking and is used to calculate form completion rates and
 * understand user behavior throughout the form flow.
 *
 * @returns A function that tracks form submission events
 *
 * @example
 * ```typescript
 * const trackFormSubmitted = useFormSubmitted()
 *
 * const handleSubmit = async (formData) => {
 *   await submitForm(formData)
 *
 *   trackFormSubmitted({
 *     formContext: {
 *       formName: 'signup',
 *       formStep: 'complete',
 *       formType: 'multi-step'
 *     },
 *     stepContext: {
 *       stepId: 'step-1',
 *       stepName: 'Step 1',
 *       stepNumber: 1,
 *     },
 *     field: [
 *       { fieldName: 'email', fieldValue: 'user@example.com' },
 *       { fieldName: 'name', fieldValue: 'John Doe' }
 *     ],
 *   })
 * }
 * ```
 */
const useFormSubmitted =
  () =>
  (props: UseFormSubmittedProps): void => {
    const { formContext, stepContext, field } = props

    if (!formContext) {
      /**
       * TODO: Add error handling for a failed form submission.
       */
      console.error('Missing required props for form submission.')
      return
    }

    const formSubmittedEventPayload = new FormSubmitted({
      formContext,
      stepContext,
      field,
    })

    TrackingService.track(formSubmittedEventPayload)
  }

export default useFormSubmitted
